import { useQuery } from '@apollo/client';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { BillingDocPage, ErrorComponents, getAdaBaseURL, useFeatureToggle, useTitle } from '@css-xp/base';
import { Refund } from '@atlassian/cssxp-types-billing';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  getRefundActions,
  getRefundAdditionalDetails,
  getRefundBreadcrumbTrail,
  getRefundIcon,
  getRefundSubheader,
} from '../refund-page-utils';
import { RefundLineItems } from './refund-line-items';
import { fetchRefundDetailQuery } from './queries/fetch-refund-query';
import { EntityWithNotes } from '@atlassian/cssxp-types-audit';
import { generatedTypesNew } from '@css-xp/graphtypes';
import {
  ENABLE_CREDIT_NOTES_TRANSACTION_ACCOUNT,
  ENABLE_RECENTLY_VISITED_HISTORY,
} from '@atlassian/cssxp-utils-generic';

interface RefundPageProps {
  title?: string;
}

const RefundPage = ({ title }: RefundPageProps): JSX.Element | null => {
  const { id } = useParams<{ id: string }>();
  // TODO:  Need to update it further for presence hook but using redux logic once user profile service done
  //const { viewers } = usePresence('invoice', id);
  //const previousId = usePrevious(id);
  const [isEnabled] = useFeatureToggle();
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(id, title);
  }
  const flag = isEnabled(ENABLE_CREDIT_NOTES_TRANSACTION_ACCOUNT);
  const { data, loading, error, refetch } = useQuery<
    generatedTypesNew.FetchRefundDetailQueryQuery,
    generatedTypesNew.FetchRefundDetailQueryQueryVariables
  >(fetchRefundDetailQuery, {
    variables: {
      id,
    },
  });
  if (loading) return <LoadingIndicator />;
  if (!data) {
    return (
      <ErrorComponents.ErrorPanel error={new ErrorComponents.NotFoundError(`Unable to find invoice with ID: ${id}`)} />
    );
  }
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  const refund = (data?.refund ? data.refund[0] : null) as Refund;
  if (refund && !refund?.refundItems) {
    return null;
  }

  const auditTrailProps = {
    entityName: EntityWithNotes.REFUND,
    id: refund?.sourceSystemId ?? id,
  };

  const adaBaseUrl = getAdaBaseURL();

  return (
    <BillingDocPage
      headerProps={{
        getIcon: getRefundIcon,
        getSubheader: getRefundSubheader.bind(null, refund),
        getActions: getRefundActions.bind(null, adaBaseUrl),
        //viewers,
      }}
      onComplete={refetch}
      billingDoc={refund}
      isFetchingBillingDoc={false}
      billingDocFetchError={null}
      getBreadcrumbTrail={getRefundBreadcrumbTrail}
      auditTrailProps={auditTrailProps}
      analyticsScreenSource={AnalyticsEventSource.REFUND_SCREEN}
      additionalDetailsContent={getRefundAdditionalDetails(refund, flag)}
      BillingDocLineItems={RefundLineItems}
      adaBaseUrl={adaBaseUrl}
      fromRefunds={true}
    />
  );
};

export default RefundPage;
